"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.Liquidity = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _sdkCore = require("@acala-network/sdk-core");
var _util = require("@polkadot/util");
var _rxjs = require("rxjs");
var _operators = require("rxjs/operators");
var _types = require("../types");
var _getChainType = require("../utils/get-chain-type");
var _errors = require("./errors");
var _storage = require("./storage");
var _calcDexPrice = require("./utils/calc-dex-price");
var _getEstimateAddLiquidityResult = require("./utils/get-estimate-add-liquidity-result");
var _getEstimateRemoveLiquidityResult = require("./utils/get-estimate-remove-liquidity-result");
var _getPoolTvl = require("./utils/get-pool-tvl");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
class Liquidity {
  constructor(api, wallet) {
    var _this = this;
    this.api = void 0;
    this.storages = void 0;
    this.wallet = void 0;
    this.consts = void 0;
    this.isReady$ = void 0;
    this.subscribePoolListByStatus = (0, _util.memoize)(function (status) {
      if (status === void 0) {
        status = 'enabled';
      }
      const filterByPoolInfos = list => {
        return list.filter(item => status === 'all' ? true : item[2] === status);
      };
      const transformStatus = list => {
        return list.map(item => {
          const rawStatus = item[1];
          const status = rawStatus.isEnabled ? 'enabled' : rawStatus.isDisabled ? 'disabled' : rawStatus.isProvisioning ? 'provision' : 'disabled';
          return [...item, status];
        });
      };
      const tokenCollections = new Set();
      return _this.storages.tradingPairs().observable.pipe((0, _operators.map)(transformStatus), (0, _operators.map)(filterByPoolInfos), (0, _operators.map)(data => {
        if (!data.length) {
          return [];
        }

        // collection all useable token to currencyId
        data.forEach(item => {
          const token1 = item[0].args[0][0];
          const token2 = item[0].args[0][1];
          tokenCollections.add((0, _sdkCore.forceToCurrencyName)(token1));
          tokenCollections.add((0, _sdkCore.forceToCurrencyName)(token2));
        });
        const tokens = Object.fromEntries(Array.from(tokenCollections).map(item => {
          return [item, _this.wallet.getToken(item)];
        }));
        return data.map(item => {
          const token1 = tokens[(0, _sdkCore.forceToCurrencyName)(item[0].args[0][0])];
          const token2 = tokens[(0, _sdkCore.forceToCurrencyName)(item[0].args[0][1])];
          return {
            token: _sdkCore.Token.fromTokens(token1, token2),
            pair: [token1, token2],
            status: item[2]
          };
        });
      }));
    });
    this.subscribePoolInfo = (0, _util.memoize)(token => {
      const targetName = (0, _sdkCore.forceToCurrencyName)(token);
      return this.subscribePoolListByStatus('all').pipe((0, _operators.map)(list => {
        const target = list.find(item => (0, _sdkCore.forceToCurrencyName)(item.token) === targetName);
        if (target) return target;
        throw new _errors.TradingPairNotFound(targetName);
      }));
    });
    this.subscribePoolPositions = (0, _util.memoize)(token => {
      const detail$ = info => {
        const issuance$ = this.storages.issuance(info.token).observable.pipe((0, _operators.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString(), info.token.decimals)));
        const poolSize$ = this.storages.liquidityPool(info.token).observable.pipe((0, _operators.map)(data => {
          return [_sdkCore.FixedPointNumber.fromInner(data[0].toString(), info.pair[0].decimals), _sdkCore.FixedPointNumber.fromInner(data[1].toString(), info.pair[1].decimals)];
        }));
        return (0, _rxjs.combineLatest)({
          issuance: issuance$,
          poolSize: poolSize$
        }).pipe((0, _operators.map)(_ref => {
          let {
            issuance,
            poolSize
          } = _ref;
          return {
            share: issuance,
            info,
            amounts: poolSize
          };
        }));
      };
      return this.subscribePoolInfo(token).pipe((0, _operators.switchMap)(detail$));
    });
    this.subscribePoolDetails = (0, _util.memoize)(token => {
      return this.subscribePoolPositions(token).pipe((0, _operators.switchMap)(positions => (0, _getPoolTvl.getPoolTVL)(positions, this.wallet).pipe((0, _operators.map)(tvl => {
        return _objectSpread(_objectSpread({}, positions), {}, {
          tvl,
          sharePrice: tvl.div(positions.share)
        });
      }))));
    });
    this.subscribeUserLiquidityDetails = (0, _util.memoize)((address, token) => {
      const getUserLiquidity$ = poolDetail => {
        return this.storages.balance(address, poolDetail.info.token).observable.pipe((0, _operators.map)(balance => {
          // only handle free part
          const userShare = _sdkCore.FixedPointNumber.fromInner(balance.free.toString(), poolDetail.info.token.decimals);
          const ratio = userShare.div(poolDetail.share);
          const owned = [poolDetail.amounts[0].mul(ratio), poolDetail.amounts[1].mul(ratio)];
          return {
            share: userShare,
            ratio,
            poolDetail,
            owned
          };
        }));
      };
      return this.subscribePoolDetails(token).pipe((0, _operators.switchMap)(getUserLiquidity$));
    });
    this.subscribeAllUserLiquidityDetails = (0, _util.memoize)(address => {
      return this.subscribePoolListByStatus().pipe((0, _operators.switchMap)(data => {
        return (0, _rxjs.combineLatest)(Object.fromEntries(data.map(item => [(0, _sdkCore.forceToCurrencyName)(item.token), this.subscribeUserLiquidityDetails(address, item.token)])));
      }));
    });
    this.subscribeEstimateAddLiquidityResult = (0, _util.memoize)((tokenA, tokenB, inputA, inputB, slippage) => {
      const dexShareToken = _sdkCore.Token.fromTokens(tokenA, tokenB);
      return this.subscribePoolDetails(dexShareToken).pipe((0, _operators.map)(detail => (0, _getEstimateAddLiquidityResult.getEstimateAddLiquidityResult)(detail, tokenA, tokenB, inputA, inputB, slippage || 0)));
    });
    this.subscribeEstimateRemoveLiquidityResult = (0, _util.memoize)((dexShareToken, removeShare, slippage) => {
      return this.subscribePoolDetails(dexShareToken).pipe((0, _operators.map)(detail => {
        return (0, _getEstimateRemoveLiquidityResult.getEstimateRemoveLiquidityResult)(detail, removeShare, slippage || 0);
      }));
    });
    this.susbscribePoolPositionOfShares = (0, _util.memoize)((dexShareToken, share) => {
      return this.subscribePoolDetails(dexShareToken).pipe((0, _operators.map)(detail => {
        const {
          share: totalShare,
          amounts
        } = detail;
        const ratio = share.div(totalShare);
        return {
          share: share,
          ratio: ratio,
          amounts: [amounts[0].mul(ratio), amounts[1].mul(ratio)],
          poolDetail: detail
        };
      }));
    });
    this.subscribeBaseTokenPrice = (0, _util.memoize)(token => {
      const name = (0, _sdkCore.forceToCurrencyName)(token);
      if (name === 'KSM') {
        return this.subscribePoolPositions((0, _sdkCore.createDexShareName)('KUSD', 'KSM')).pipe((0, _operators.map)(positions => {
          return positions.amounts[0].div(positions.amounts[1]);
        }));
      }
      if (name === 'DOT') {
        return this.subscribePoolPositions((0, _sdkCore.createDexShareName)('AUSD', 'DOT')).pipe((0, _operators.map)(positions => {
          return positions.amounts[0].div(positions.amounts[1]);
        }));
      }
      if (name === 'KAR') {
        return this.subscribePoolPositions((0, _sdkCore.createDexShareName)('KAR', 'KUSD')).pipe((0, _operators.map)(positions => {
          return positions.amounts[0].div(positions.amounts[1]);
        }));
      }
      if (name === 'ACA') {
        return this.subscribePoolPositions((0, _sdkCore.createDexShareName)('ACA', 'AUSD')).pipe((0, _operators.map)(positions => {
          return positions.amounts[0].div(positions.amounts[1]);
        }));
      }
      if (name === 'sa://0') {
        return this.subscribeBaseTokenPrice('KSM');
      }
      return (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
    });
    this.subscribeDexPrice = (0, _util.memoize)(token => {
      const karuraBaseTokens = ['KSM', 'KAR', 'KUSD', 'sa://0'];
      // TODO: show add DOT as base token when lp://AUSD/DOT is open
      const acalaBaseTokens = ['ACA', 'AUSD'];
      const chainType = (0, _getChainType.getChainType)(this.consts.runtimeChain);
      const name = (0, _sdkCore.forceToCurrencyName)(token);
      const baseTokens = chainType === _types.ChainType.ACALA || chainType === _types.ChainType.MANDALA ? acalaBaseTokens : karuraBaseTokens;
      if (name === 'KUSD' || name === 'AUSD') {
        return (0, _rxjs.of)(_sdkCore.FixedPointNumber.ONE);
      }
      if (baseTokens.find(i => i === name)) {
        return this.subscribeBaseTokenPrice(name);
      }

      // create base pools should be sorted
      const basePools = baseTokens.map(i => {
        const [token0, token1] = _sdkCore.Token.sortTokenNames(name, i);
        return (0, _sdkCore.createDexShareName)(token0, token1);
      });

      // filter opened pools
      const availableBasePools$ = (0, _rxjs.combineLatest)(basePools.map(pool => this.subscribeIsPoolEnabled(pool))).pipe((0, _operators.map)(result => basePools.map((pool, i) => ({
        pool,
        isOpen: result[i]
      })).filter(item => item.isOpen)));
      return availableBasePools$.pipe((0, _operators.switchMap)(status => {
        if (status.length === 0) return (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
        return (0, _rxjs.combineLatest)(status.map(i => this.subscribePoolPositions(i.pool))).pipe((0, _operators.switchMap)(list => (0, _calcDexPrice.calcDexPrice)(token, list, this.wallet)));
      }));
    });
    this.getDexPrice = token => {
      return (0, _rxjs.firstValueFrom)(this.subscribeDexPrice(token));
    };
    this.api = api;
    this.storages = (0, _storage.createStorages)(this.api);
    this.wallet = wallet;
    this.isReady$ = new _rxjs.BehaviorSubject(true);
    this.consts = {
      runtimeChain: this.api.runtimeChain.toString()
    };
  }
  get isReady() {
    return (0, _rxjs.firstValueFrom)(this.isReady$.pipe((0, _operators.filter)(i => i)));
  }

  /**
   * @name subscribePoolListByStatus
   * @description get pool list which filtered by status
   */

  getPoolListByStatus(status) {
    if (status === void 0) {
      status = 'enabled';
    }
    return (0, _rxjs.firstValueFrom)(this.subscribePoolListByStatus(status));
  }

  /**
   * @name subscribePoolInfo
   * @description get pool infomation of `token`
   */

  getPoolInfo(token) {
    return (0, _rxjs.firstValueFrom)(this.subscribePoolInfo(token));
  }
  getPoolPositions(token) {
    return (0, _rxjs.firstValueFrom)(this.subscribePoolPositions(token));
  }

  /**
   * @name subscribePoolDetails
   * @description get pool detail of `token`
   */

  getPoolDetail(token) {
    return (0, _rxjs.firstValueFrom)(this.subscribePoolDetails(token));
  }

  /**
   * @name subscribeAllEnabledPoolDetails
   * @description get all enable pool information
   */
  subscribeAllEnabledPoolDetails() {
    return this.subscribePoolListByStatus().pipe((0, _operators.switchMap)(data => {
      return (0, _rxjs.combineLatest)(Object.fromEntries(data.map(item => [(0, _sdkCore.forceToCurrencyName)(item.token), this.subscribePoolDetails(item.token)])));
    }));
  }

  /**
   * @name getAllEnabledPoolDetails
   * @description get all enable pool information
   */
  getAllEnabledPoolDetails() {
    return (0, _rxjs.firstValueFrom)(this.subscribeAllEnabledPoolDetails());
  }

  /**
   * @name subscribeUserLiquidityDetails
   * @description get `user` `token` pool information
   */

  getUserLiquidityDetails(address, token) {
    return (0, _rxjs.firstValueFrom)(this.subscribeUserLiquidityDetails(address, token));
  }

  /**
   * @name subscribeAllUserLiquidityDetails
   * @description subscribe all `user` pools information
   */

  getAllUserLiquidityDetails(address) {
    return (0, _rxjs.firstValueFrom)(this.subscribeAllUserLiquidityDetails(address));
  }

  /**
   * @name subscribeEstimateAddLiquidityResult
   * @description estimate add liquidity result
   */

  getEstimateAddLiquidityResult(tokenA, tokenB, inputA, inputB, slippage) {
    return (0, _rxjs.firstValueFrom)(this.subscribeEstimateAddLiquidityResult(tokenA, tokenB, inputA, inputB, slippage));
  }

  /**
   * @name subscribeEstimateRemoveLiquidity
   * @description subscribe estimate remove liquidity result
   */

  getEstimateRemoveLiquidityResult(dexShareToken, removeShare, slippage) {
    return (0, _rxjs.firstValueFrom)(this.subscribeEstimateRemoveLiquidityResult(dexShareToken, removeShare, slippage));
  }

  /**
   * @name subscribePoolSizeOfShares
   * @description subscribe `dexShareToken` pool size of `shares`
   */

  getPoolPositionOfShares(dexShareToken, share) {
    return (0, _rxjs.firstValueFrom)(this.susbscribePoolPositionOfShares(dexShareToken, share));
  }
  subscribeIsPoolEnabled(token0, token1) {
    const enabled$ = this.subscribePoolListByStatus();
    return enabled$.pipe((0, _operators.map)(list => {
      let name = typeof token0 === 'string' ? token0 : (0, _sdkCore.forceToCurrencyName)(token0);
      if (token1) {
        name = (0, _sdkCore.createDexShareName)((0, _sdkCore.forceToCurrencyName)(token0), (0, _sdkCore.forceToCurrencyName)(token1));
      }
      return !!list.find(i => (0, _sdkCore.forceToCurrencyName)(i.token) === name);
    }));
  }
  getIsPoolEnabled(token0, token1) {
    return (0, _rxjs.firstValueFrom)(this.subscribeIsPoolEnabled(token0, token1));
  }
}
exports.Liquidity = Liquidity;